import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './client';
import { monthlyKeys } from './plan-matriz-monthly';
import type {
  ClosureReadiness,
  ClosureLogResponse,
  ExecuteClosureResult,
  ClosureListFilter,
  ExecuteClosureInput,
  ReprogramacionResult,
} from '@sime/shared';

// --- Response Types ---

interface ClosureReadinessResponse {
  success: true;
  data: ClosureReadiness;
}

interface ClosureHistoryResponse {
  success: true;
  data: ClosureLogResponse[];
}

interface ExecuteClosureResponse {
  success: true;
  data: ExecuteClosureResult;
}

interface ReprogramacionResponse {
  success: true;
  data: ReprogramacionResult;
}

interface PackageVerification {
  ambitoId: number;
  periodo: string;
  totalApproved: number;
  withSignedPdf: number;
  missingPdf: Array<{ ticketId: number; correlativo: string; reason: string }>;
  ready: boolean;
  /** Non-terminal tickets in the period, not included in the package (partial closure). */
  pendingTickets: number;
}

interface PackageVerificationResponse {
  success: true;
  data: PackageVerification;
}

interface RetryFailedResponse {
  success: true;
  data: { total: number; regenerated: number; failed: number };
}

interface PackageDownloadResponse {
  success: true;
  data: { downloadUrl: string };
}

// --- Signature Pipeline Types ---

interface SignaturePipelineTicket {
  id: number;
  correlativo: string;
  /** Activity name (planMatriz.nombre ?? servicio). Shown next to the
   *  correlativo in the "Enviar a Firma" modal so coordinators can tell
   *  tickets apart by more than the number. */
  nombre?: string | null;
  loteId?: number;
}

export interface LoteEmailStatus {
  id: number;
  status: string;
  totalTickets: number;
  emailSentAt: string | null;
  emailDeliveredAt: string | null;
  emailOpenedAt: string | null;
  emailBouncedAt: string | null;
}

export interface SignaturePipelineStats {
  ambitoId: number;
  periodo: string;
  pendiente: number;
  enLote: number;
  enviado: number;
  firmado: number;
  total: number;
  tickets: {
    pendiente: SignaturePipelineTicket[];
    enLote: SignaturePipelineTicket[];
    enviado: SignaturePipelineTicket[];
    firmado: SignaturePipelineTicket[];
  };
  lotes: LoteEmailStatus[];
}

interface SignaturePipelineResponse {
  success: true;
  data: SignaturePipelineStats;
}

// --- Query Keys ---

export const closureKeys = {
  all: ['closure'] as const,
  readiness: (ambitoId: number, periodo: string) =>
    [...closureKeys.all, 'readiness', ambitoId, periodo] as const,
  history: (filter: Partial<ClosureListFilter>) =>
    [...closureKeys.all, 'history', filter] as const,
  packageVerify: (ambitoId: number, periodo: string) =>
    [...closureKeys.all, 'package-verify', ambitoId, periodo] as const,
  signaturePipeline: (ambitoId: number, periodo: string) =>
    [...closureKeys.all, 'signature-pipeline', ambitoId, periodo] as const,
};

// --- Query Hooks ---

export function useClosureReadiness(ambitoId: number, periodo: string) {
  return useQuery({
    queryKey: closureKeys.readiness(ambitoId, periodo),
    queryFn: () =>
      apiClient<ClosureReadinessResponse>(
        `/closure/readiness?ambitoId=${ambitoId}&periodo=${periodo}`,
      ),
    enabled: ambitoId > 0 && periodo.length > 0,
    refetchInterval: 30_000,
  });
}

export function useSignaturePipeline(ambitoId: number, periodo: string) {
  return useQuery({
    queryKey: closureKeys.signaturePipeline(ambitoId, periodo),
    queryFn: () =>
      apiClient<SignaturePipelineResponse>(
        `/closure/signature-pipeline?ambitoId=${ambitoId}&periodo=${periodo}`,
      ),
    enabled: ambitoId > 0 && periodo.length > 0,
    refetchInterval: 30_000,
  });
}

export function useClosureHistory(
  filter: Partial<ClosureListFilter> = {},
  options?: {
    // number | false, or a callback (so callers can poll only while a closure is
    // IN_PROGRESS). Typed structurally against what the callback reads.
    refetchInterval?:
      | number
      | false
      | ((query: { state: { data?: ClosureHistoryResponse } }) => number | false);
  },
) {
  const params = new URLSearchParams();
  if (filter.ambitoId) params.set('ambitoId', String(filter.ambitoId));
  if (filter.periodo) params.set('periodo', filter.periodo);
  if (filter.status) params.set('status', filter.status);

  const qs = params.toString();
  const endpoint = `/closure/history${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: closureKeys.history(filter),
    queryFn: () => apiClient<ClosureHistoryResponse>(endpoint),
    refetchInterval: options?.refetchInterval,
  });
}

export function usePackageVerification(ambitoId: number, periodo: string) {
  return useQuery({
    queryKey: closureKeys.packageVerify(ambitoId, periodo),
    queryFn: () =>
      apiClient<PackageVerificationResponse>(
        `/signatures/package/verify?ambitoId=${ambitoId}&periodo=${periodo}`,
      ),
    enabled: ambitoId > 0 && periodo.length > 0,
  });
}

// --- Mutation Hooks ---

export function useExecuteClosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ExecuteClosureInput) =>
      apiClient<ExecuteClosureResponse>('/closure/execute', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: closureKeys.readiness(variables.ambitoId, variables.periodo),
      });
      void queryClient.invalidateQueries({
        queryKey: closureKeys.history({}),
      });
      // Refresh package verification (pending counts / ready state) on the package page.
      void queryClient.invalidateQueries({
        queryKey: closureKeys.packageVerify(variables.ambitoId, variables.periodo),
      });
      // Closure can transition many tickets in bulk (e.g. EJECUTADO→APROBADO
      // when signing the package). Calendar grid must repaint accordingly.
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
    },
  });
}

/**
 * Fetch the presigned URL of a closure package. The endpoint answers JSON
 * (not a redirect) because the JWT travels in the Authorization header —
 * window.open against the API path would arrive unauthenticated. Callers
 * trigger the download themselves (anchor click, mirrors downloadTicketPdf).
 */
export function usePackageDownloadUrl() {
  return useMutation({
    mutationFn: async (closureLogId: number) => {
      const res = await apiClient<PackageDownloadResponse>(
        `/closure/${String(closureLogId)}/download`,
      );
      return res.data.downloadUrl;
    },
  });
}

export function useRetryFailedPdfs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { ambitoId: number; periodo: string }) =>
      apiClient<RetryFailedResponse>('/signatures/package/retry-failed', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: closureKeys.readiness(variables.ambitoId, variables.periodo),
      });
      void queryClient.invalidateQueries({
        queryKey: closureKeys.packageVerify(variables.ambitoId, variables.periodo),
      });
    },
  });
}

export function useTriggerReprogramacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { ambitoId?: number; periodo?: string }) =>
      apiClient<ReprogramacionResponse>('/closure/reprogramacion/trigger', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: closureKeys.all });
      // Auto-reprogramacion changes ot.fechaProgramada and may transition
      // tickets to REPROGRAMADO. Both move the card between cells and recolor it.
      void queryClient.invalidateQueries({ queryKey: monthlyKeys.all });
    },
  });
}

// Re-export types for page components
export type { PackageVerification, PackageVerificationResponse };
